/**
 * Read-only renderer for a comment body.
 *
 * Plain text with two inline token grammars replaced by styled chips:
 * `@mention` tokens (e.g. `@Alice`) and workflow node-reference tokens (e.g.
 * `@Click Submit`, styled distinctly with a step icon). Both grammars can
 * appear in the same body, so matches from both regexes are collected and
 * walked in a single ordered pass. Preserves whitespace/newlines via
 * `whitespace-pre-wrap`.
 */

import React from 'react';
import { mentionRegex, nodeRefRegex } from '@chatsdet/types';
import { FlowArrow } from '@phosphor-icons/react';

interface CommentBodyProps {
  body: string;
  className?: string;
  /** When provided, node-reference chips render as clickable buttons. */
  onNodeClick?: (nodeId: string) => void;
}

type Token =
  | { kind: 'mention'; displayName: string; start: number; end: number; raw: string }
  | { kind: 'node'; label: string; nodeId: string; start: number; end: number; raw: string };

export function CommentBody({ body, className, onNodeClick }: CommentBodyProps) {
  const nodes = React.useMemo(() => renderBody(body, onNodeClick), [body, onNodeClick]);
  return (
    <span className={className ?? 'text-sm text-foreground whitespace-pre-wrap'}>{nodes}</span>
  );
}

function collectTokens(body: string): Token[] {
  const tokens: Token[] = [];

  const mentions = mentionRegex();
  let match: RegExpExecArray | null;
  while ((match = mentions.exec(body)) !== null) {
    tokens.push({
      kind: 'mention',
      displayName: match[1]!,
      start: match.index,
      end: match.index + match[0].length,
      raw: match[0],
    });
  }

  const nodeRefs = nodeRefRegex();
  while ((match = nodeRefs.exec(body)) !== null) {
    tokens.push({
      kind: 'node',
      label: match[1]!,
      nodeId: match[2]!.toLowerCase(),
      start: match.index,
      end: match.index + match[0].length,
      raw: match[0],
    });
  }

  tokens.sort((a, b) => a.start - b.start);
  return tokens;
}

function renderBody(
  body: string,
  onNodeClick: ((nodeId: string) => void) | undefined
): React.ReactNode[] {
  const tokens = collectTokens(body);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  for (const token of tokens) {
    if (token.start < cursor) continue; // overlap guard, shouldn't happen given disjoint grammars
    if (token.start > cursor) {
      out.push(body.slice(cursor, token.start));
    }

    if (token.kind === 'mention') {
      out.push(
        <span
          key={`m-${key++}`}
          className="inline-flex items-baseline px-1 py-0.5 rounded text-xs font-medium border bg-primary/10 text-primary border-primary/30"
        >
          @{token.displayName}
        </span>
      );
    } else {
      const chipClassName =
        'inline-flex items-center gap-1 px-1 py-0.5 rounded text-xs font-medium border bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/30';
      out.push(
        onNodeClick ? (
          <button
            key={`n-${key++}`}
            type="button"
            onClick={() => onNodeClick(token.nodeId)}
            className={`${chipClassName} cursor-pointer hover:bg-cyan-500/20 transition-colors`}
          >
            <FlowArrow size={11} weight="bold" />
            {token.label}
          </button>
        ) : (
          <span key={`n-${key++}`} className={chipClassName}>
            <FlowArrow size={11} weight="bold" />
            {token.label}
          </span>
        )
      );
    }

    cursor = token.end;
  }

  if (cursor < body.length) out.push(body.slice(cursor));
  return out;
}
